import { io, type Socket } from 'socket.io-client';
import { SocketEvents } from '@tcrd/shared';
import { env } from './env';
import { refreshSession, tokens } from './api';

type Handler = (payload: Record<string, unknown>) => void;

const LAST_SEQ_KEY = 'tcrd.lastSeq';
let socket: Socket | null = null;
const handlers = new Map<string, Set<Handler>>();
const statusListeners = new Set<(connected: boolean) => void>();

function readSeq(): string | null {
  try {
    return localStorage.getItem(LAST_SEQ_KEY);
  } catch {
    return null;
  }
}
function writeSeq(seq: string | undefined) {
  if (!seq) return;
  try {
    const prev = readSeq();
    if (!prev || BigInt(seq) > BigInt(prev)) localStorage.setItem(LAST_SEQ_KEY, seq);
  } catch {
    /* sin almacenamiento */
  }
}

function dispatch(event: string, payload: Record<string, unknown>) {
  writeSeq(payload.seq as string | undefined);
  handlers.get(event)?.forEach((h) => h(payload));
}

/** Eventos persistentes que se reenvían tras reconectar. */
const PERSISTENT = [SocketEvents.notification, SocketEvents.rideRequestUpdated, SocketEvents.quoteUpdated, SocketEvents.rideUpdated, SocketEvents.chatMessage];

export const realtime = {
  connect() {
    if (socket || !tokens.access) return;
    socket = io(env.apiUrl, {
      path: '/socket.io',
      transports: ['websocket'],
      // Se evalúa en cada (re)conexión: siempre con el access token vigente.
      auth: (cb) => cb({ token: tokens.access }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15_000,
    });
    socket.on('connect', async () => {
      statusListeners.forEach((l) => l(true));
      // Recuperar lo emitido mientras estábamos desconectados.
      const res = (await socket!.timeout(8000).emitWithAck(SocketEvents.resume, { lastSeq: readSeq() }).catch(() => null)) as {
        events: Array<{ seq: string; event: string; payload: Record<string, unknown> }>;
        lastSeq: string;
      } | null;
      res?.events.forEach((e) => dispatch(e.event, { ...e.payload, seq: e.seq, replayed: true }));
      writeSeq(res?.lastSeq);
    });
    socket.on('disconnect', () => statusListeners.forEach((l) => l(false)));
    socket.on('connect_error', async (err) => {
      if (err.message === 'UNAUTHORIZED') await refreshSession();
    });
    socket.on('auth.expired', async () => {
      await refreshSession();
      socket?.connect();
    });
    for (const ev of PERSISTENT) socket.on(ev, (p: Record<string, unknown>) => dispatch(ev, p));
    // Eventos efímeros (GPS, escribiendo…)
    for (const ev of [SocketEvents.rideLocation, SocketEvents.chatTyping, SocketEvents.chatRead]) socket.on(ev, (p: Record<string, unknown>) => dispatch(ev, p));
  },

  disconnect() {
    socket?.close();
    socket = null;
  },

  on(event: string, h: Handler) {
    if (!handlers.has(event)) handlers.set(event, new Set());
    handlers.get(event)!.add(h);
    return () => handlers.get(event)?.delete(h);
  },

  onStatus(fn: (connected: boolean) => void) {
    statusListeners.add(fn);
    return () => statusListeners.delete(fn);
  },

  get connected() {
    return !!socket?.connected;
  },

  async emit<T = unknown>(event: string, payload: unknown, timeoutMs = 8000): Promise<T | null> {
    if (!socket?.connected) return null;
    return (await socket.timeout(timeoutMs).emitWithAck(event, payload).catch(() => null)) as T | null;
  },

  /** Envío sin confirmación (GPS de alta frecuencia). */
  send(event: string, payload: unknown) {
    socket?.volatile.emit(event, payload);
  },
};

tokens.onChange((authenticated) => {
  if (!authenticated) realtime.disconnect();
});
